import { useMemo } from 'react';
import type { TextItemEntry, TimelineEntry } from '../types/types';

interface TextGroup {
  entries: TextItemEntry[];
  lastPosition: number;
}

interface TextGroupInfo {
  isLastInGroup: boolean;
  groupContent: string;
  groupEntries: TextItemEntry[];
}

interface UseTextGroupingResult {
  textGroups: Map<string, TextGroup>;
  getTextGroupInfo: (entry: TextItemEntry) => TextGroupInfo | null;
}

/**
 * Groups text entries by their groupId for EVIDENCE mode rendering.
 * All text blocks with the same groupId render together as inline elements.
 */
export const useTextGrouping = (timeline: TimelineEntry[]): UseTextGroupingResult => {
  const textGroups = useMemo(() => {
    const groups = new Map<string, TextGroup>();

    timeline.forEach((entry) => {
      if (!entry?.type) return;

      if (entry.type === 'text_item' && entry.groupId) {
        const existing = groups.get(entry.groupId);
        if (existing) {
          existing.entries.push(entry);
          existing.lastPosition = entry.position;
        } else {
          groups.set(entry.groupId, {
            entries: [entry],
            lastPosition: entry.position,
          });
        }
      }
    });

    return groups;
  }, [timeline]);

  const getTextGroupInfo = (entry: TextItemEntry): TextGroupInfo | null => {
    if (!entry.groupId) return null;

    const group = textGroups.get(entry.groupId);
    if (group) {
      const isLastInGroup = entry.position === group.lastPosition;
      const groupContent = group.entries.map((e) => e.content).join('');
      return { isLastInGroup, groupContent, groupEntries: group.entries };
    }
    return null;
  };

  return { textGroups, getTextGroupInfo };
};
